import {inject, Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {
  LoggerService
} from '../logging/logger.service';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

const READY_TIMEOUT_MS = 5000;

/**
 * Dynamically loads and wraps the Meta Pixel on web.
 * No-op on native platforms. Gracefully handles ad
 * blockers.
 */
@Injectable({
  providedIn: 'root'
})
export class MetaPixelService {
  private readonly logger = inject(LoggerService);
  private loaded = false;
  private readyResolve?: () => void;
  private readyPromise?: Promise<void>;

  initialise(pixelId: string): void {
    if (Capacitor.getPlatform() !== 'web') {
      this.logger.debug(
        'MetaPixel: Skipping on native platform'
      );
      return;
    }

    if (this.loaded) {
      this.logger.debug(
        'MetaPixel: Already loaded'
      );
      return;
    }

    if (!pixelId) {
      this.logger.warn(
        'MetaPixel: No pixel ID provided'
      );
      return;
    }

    this.readyPromise = new Promise<void>(
      resolve => { this.readyResolve = resolve; }
    );

    try {
      this.injectScript(pixelId);
      this.loaded = true;
      this.logger.info(
        'MetaPixel: Initialised successfully'
      );
    } catch (error) {
      this.readyResolve?.();
      this.logger.warn(
        'MetaPixel: Failed to initialise '
        + '(ad blocker may be active)',
        error
      );
    }
  }

  /**
   * Resolves when fbevents.js has loaded, or after
   * a timeout if the script fails. Resolves
   * immediately if the pixel was not initialised.
   */
  whenReady(): Promise<void> {
    return this.readyPromise ?? Promise.resolve();
  }

  trackEvent(
    eventName: string,
    params?: Record<string, any>
  ): void {
    if (!this.loaded || !window.fbq) {
      return;
    }

    try {
      if (params) {
        window.fbq('track', eventName, params);
      } else {
        window.fbq('track', eventName);
      }
      this.logger.debug(
        `MetaPixel: Tracked '${eventName}'`
      );
    } catch (error) {
      this.logger.warn(
        'MetaPixel: Failed to track event',
        error
      );
    }
  }

  private injectScript(pixelId: string): void {
    const f = window as any;
    const n: any = (f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];

    const script =
      document.createElement('script');
    script.async = true;
    script.src =
      'https://connect.facebook.net'
      + '/en_US/fbevents.js';

    const timeoutId = setTimeout(() => {
      this.readyResolve?.();
    }, READY_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeoutId);
      this.logger.debug(
        'MetaPixel: fbevents.js loaded'
      );
      this.readyResolve?.();
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      this.logger.warn(
        'MetaPixel: fbevents.js failed to load'
      );
      this.readyResolve?.();
    };

    const firstScript =
      document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(
      script, firstScript
    );

    window.fbq!('init', pixelId);
    window.fbq!('track', 'PageView');
  }
}
